import { Trans } from '@lingui/react'
import classNames from 'classnames'
import type { VFC } from 'react'

import {
  PRESCRIPTION_TYPE_TO_PRICE_TYPE,
  PRICE_TYPES,
} from 'shared/consts/productTemplates'
import { getPriceForType } from 'shared/utils/productTemplateUtils'
import DiscountBadge from 'views/components/atoms/DiscountBadge'
import {
  currencyToSymbol,
  formatPriceString,
} from 'views/components/atoms/Price/helpers'
import {
  ListPrice,
  RetailPrice,
} from 'views/components/organisms/ProductVariants/ProductVariant/ProductVariantComponents'
import { type AttributePickers } from 'views/containers/browser/ProductVariantsAttributesContainer/AttributePicker'
import type { Variant } from 'views/containers/browser/ProductVariantsAttributesContainer/index'
import { getVariantPrices } from 'views/utils/productUtils'
import { usePublicConfig } from 'views/utils/shopAndRuntimeConfigUtils'

enum PrescriptionTypes {
  PRESCRIPTION_TYPE_NO_PRESCRIPTION = 'noPrescription',
  PRESCRIPTION_TYPE_INSURANCE = 'insurancePrescription',
  PRESCRIPTION_TYPE_FREE = 'freePrescription',
  PRESCRIPTION_TYPE_PRIVAT = 'privatePrescription',
}

interface AttributePickerSingleAttributeProps extends AttributePickers {
  variant?: Variant
  prescriptionType: string
  isSelected: boolean
  hasAdditionalCharge: boolean
  variantAttributeType: string
  packageSizeAttribute: boolean
}
interface BadgeProps {
  displayDiscount?: number
  color?: string
  badgeLabel: JSX.Element
  showSupNotes?: boolean
}

const AttributePickerSingleAttribute: VFC<
  AttributePickerSingleAttributeProps
> = ({
  pickerValue,
  id,
  styles,
  variant,
  prescriptionType,
  isSelected,
  hasAdditionalCharge,
  variantAttributeType,
  packageSizeAttribute,
}) => {
  const { locale } = usePublicConfig()
  const hasColor =
    variantAttributeType.toLowerCase() === 'color' && pickerValue.includes('#')
  let splitColor = ['']
  if (hasColor) {
    splitColor = pickerValue.split('#')
  }
  const hasBonus =
    prescriptionType === PrescriptionTypes.PRESCRIPTION_TYPE_PRIVAT

  const discount = variant?.discount

  let prices = variant?.prices

  const variantOffer = variant?.offers?.find(offer =>
    variant.offerId
      ? variant.offerId === offer.offerId
      : offer.offerFlags?.includes('bestPrice')
  )

  prices = getVariantPrices({
    variantOffer,
    prices,
  })

  const isRetailPricePresent = prices?.some(price => {
    return price.type === PRICE_TYPES.RETAIL
  })

  const listPriceAmount = getPriceForType(prices, PRICE_TYPES.LIST)

  const prescriptionTypeConverter = (
    Object.values(PrescriptionTypes) as PrescriptionTypes[]
  ).find(type => type === prescriptionType)

  const pickerValueStyles = classNames(
    'a-singleVariant--pickerValueFont--font u-no-margin--bottom',
    {
      'colorPicker': hasColor,
      'l-flex': hasColor || !packageSizeAttribute,
      'l-flex l-flex--center': packageSizeAttribute,
    }
  )
  const colorPickerColorStyles = classNames('colorPickerColor', {
    'whiteColorPickerColor':
      splitColor[1]?.toLowerCase() === 'fff' ||
      splitColor[1]?.toLowerCase() === 'ffffff',
  })

  const discountBadgeClasses = classNames('u-font-weight--bold', {
    'a-CircleBadge--border': isSelected,
    'a-DiscountBadgeBonus-padding': hasBonus,
    'a-DiscountBadge-padding o-Menu__link': !hasBonus,
  })

  const discountBadgeWrapperClasses = classNames('discountBadgeWrapper', {
    'a-DiscountBadgeBonusWrapper--top': hasBonus,
    'discountBadgeWrapper_notPackageSize': !packageSizeAttribute,
  })

  const retailPriceClasses = classNames('u-font-weight--bold u-no-margin ', {
    'a-singleVariant--retailPrice--packageSize--font': packageSizeAttribute,
    'a-singleVariant--retailPrice--font': !packageSizeAttribute,
    'u-visibility--hidden': !isRetailPricePresent,
  })

  const buttonStyles = classNames(
    styles,
    'o-ProductVariantsAttributePickerSingleAttribute l-flex l-flex--center',
    {
      'o-ProductVariantsAttributePickerSingleAttribute--packageSize--width':
        packageSizeAttribute,
      'o-ProductVariantsAttributePickerSingleAttribute--nonPackageSize--size':
        !packageSizeAttribute,
      'u-margin-small--top': hasAdditionalCharge,
      'u-margin-large--top u-margin-small--bottom': !hasAdditionalCharge,
      'o-ProductVariantsAttributePickerSingleAttribute-bonus--size': hasBonus,
    }
  )

  const pricesWrapperStyles = classNames(
    'l-flex l-flex--vertical-center@small-tiny u-no-margin',
    {
      'l-flex--vertical': packageSizeAttribute,
      'l-flex--distribute-space a-h3': !packageSizeAttribute,
    }
  )

  const getBadge = ({
    badgeLabel,
    displayDiscount = 0,
    color = 'red',
    showSupNotes = false,
  }: BadgeProps) => (
    <DiscountBadge
      discount={displayDiscount}
      ellipse
      color={color}
      rounded
      smaller
      className={discountBadgeClasses}
      border
      showSupNotes={showSupNotes}
      bonus={hasBonus}
    >
      <span suppressHydrationWarning className="u-no-wrap">
        {badgeLabel}
      </span>
    </DiscountBadge>
  )

  const realPriceAmount =
    prescriptionTypeConverter &&
    getPriceForType(
      prices,
      PRESCRIPTION_TYPE_TO_PRICE_TYPE[prescriptionTypeConverter]
    )

  const retailPriceProps = {
    amount: realPriceAmount.amount,
    prescriptionType,
  }
  const listPriceProps = {
    amount:
      listPriceAmount?.amount > realPriceAmount.amount && !!discount
        ? listPriceAmount.amount
        : null,
    noLineThrough:
      Boolean(listPriceAmount) &&
      Boolean(realPriceAmount) &&
      listPriceAmount.amount === realPriceAmount.amount,
  }
  const listPriceClasses = classNames(
    'u-font-weight--normal u-color--dark o-Menu__link',
    {
      'a-ListPrice--color--background': isSelected,
      'u-visibility--hidden': !isRetailPricePresent,
    }
  )

  const listPriceNoPrescriptionClasses = classNames(
    'u-text-decoration--line-through o-SearchProductListItem__prices__list-price u-color--grey a-singleVariant--listPrice--font',
    {
      'u-padding-x-small': packageSizeAttribute,
      'u-visibility--hidden u-padding-x-small':
        !isRetailPricePresent ||
        listPriceAmount?.amount <= realPriceAmount.amount,
    }
  )

  let listPrice

  const getPriceBadgeBasedOdPrescriptionType = () => {
    switch (prescriptionType) {
      case PrescriptionTypes.PRESCRIPTION_TYPE_NO_PRESCRIPTION: {
        listPrice = (
          <span>
            <span className={listPriceNoPrescriptionClasses}>
              <ListPrice {...listPriceProps} />
            </span>
          </span>
        )
        if (!discount) {
          return null
        }
        const showSupNotes = true
        const badgeLabel = <>-{discount}%</>
        const displayDiscount = discount

        return getBadge({ displayDiscount, badgeLabel, showSupNotes })
      }

      case PrescriptionTypes.PRESCRIPTION_TYPE_PRIVAT: {
        const privatePrescriptionPrice = prices?.find(price => {
          return price.type === PrescriptionTypes.PRESCRIPTION_TYPE_PRIVAT
        })
        const currencySymbol = currencyToSymbol(
          privatePrescriptionPrice?.currency
        )
        const bonusAmount = privatePrescriptionPrice?.bonus
        const bonusPriceString = formatPriceString({
          amount: bonusAmount,
          locale,
          currencySymbol,
          symbolRight: false,
        })
        const displayDiscount = bonusAmount
        const color = 'rxgreen'
        const badgeLabel = (
          <span className="u-font-weight--normal u-no-margin--bottom l-flex l-flex--vertical">
            <span className="a-DiscountBadgeBonus-fontsize">
              <Trans id="account.orders.overview.bonus" />
            </span>
            <span className="u-font-weight--bold a-h3 u-no-margin--bottom a-BonusAmount--fontSize">
              {bonusPriceString}
            </span>
          </span>
        )
        return bonusAmount
          ? getBadge({ color, displayDiscount, badgeLabel })
          : null
      }

      default:
        listPrice = (
          <span className={listPriceClasses}>
            <Trans id="general.label.additionalCharge" />
          </span>
        )
        return null
    }
  }

  return (
    <button
      className={buttonStyles}
      id={id}
      name="variantAttribute"
      title={hasColor ? splitColor[0] : pickerValue}
      value={pickerValue}
      data-qa-id={`attributePicker-${id}`}
    >
      <span className={discountBadgeWrapperClasses}>
        {getPriceBadgeBasedOdPrescriptionType()}
      </span>
      <span className="a-full-width">
        <span className={pickerValueStyles}>
          {hasColor ? (
            <>
              <span
                style={{ backgroundColor: `#${splitColor[1]}` }}
                className={colorPickerColorStyles}
              ></span>
              <span
                id={id}
                className="buttonValueDisplay a-h4 u-font-weight--bold u-text-truncate"
              >
                {splitColor[0]}
              </span>
            </>
          ) : (
            <span className="u-text-truncate">{pickerValue}</span>
          )}
        </span>
        <span className={pricesWrapperStyles}>
          {listPrice}
          <span
            className={retailPriceClasses}
            data-qa-id="entry-price"
            id="retailPrice"
          >
            <RetailPrice {...retailPriceProps} />
          </span>
        </span>
      </span>
    </button>
  )
}

export default AttributePickerSingleAttribute
